import { MenuAlt2Icon } from '@heroicons/react/outline';
import { ChevronDownIcon } from '@heroicons/react/solid';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'components/atoms/Button';
import Dropdown from 'components/atoms/Dropdown';
import UserAvatar from 'components/atoms/UserAvatar';
import WorkshopSearchInput from 'components/orgamisms/WorkshopSearchInput';
import { NAVIGATION_ROUTES } from 'constants/navigationPaths';
import useUser from 'hooks/useUser';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { tw } from 'utils';
import { screenIs } from 'utils/uiUtils';

NavbarWorkshop.propTypes = {
  userNavigation: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    })
  ),
  setMobileMenuOpen: PropTypes.func,
  search: PropTypes.bool,
  searchQuery: PropTypes.string
};
export default function NavbarWorkshop({
  userNavigation,
  setMobileMenuOpen,
  logo,
  search = false,
  customActions = [],
  displaySearchResult,
  searchQuery
}) {
  const { t } = useTranslation();
  const isSmall = !screenIs('md');
  const { push, asPath } = useRouter();
  const { user } = useUser();

  return (
    <header className={tw('w-full z-20')}>
      <div
        className={tw(
          'relative z-10 flex-shrink-0 h-16 bg-white border-b border-gray-200 shadow-sm flex'
        )}>
        {setMobileMenuOpen && (
          <button
            type="button"
            className={tw(
              'border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden'
            )}
            onClick={() => setMobileMenuOpen(true)}>
            <span className="sr-only">Open sidebar</span>
            <MenuAlt2Icon className={tw('h-6 w-6')} aria-hidden="true" />
          </button>
        )}
        <div className={tw('max-w-7xl flex-1 flex justify-between px-4 sm:px-6 mx-auto')}>
          {logo && (
            <div className={tw('flex items-center mr-4')}>
              <button
                data-testid={'navbar-logo'}
                onClick={() => push(NAVIGATION_ROUTES.Home)}
                className={tw('focus:outline-none outline-none')}>
                <img
                  className={tw('block sm:hidden h-6 w-auto')}
                  src={'/assets/logo/logo-mini-black.svg'}
                  alt={'vidohouse'}
                />
                <img
                  className={'hidden sm:block w-auto h-10'}
                  src={'/assets/logo/logo-full.svg'}
                  alt="vidohouse logo"
                />
              </button>
            </div>
          )}
          <div />
          {/* {search && <SearchInput border="border-none" placeholder="Search for workshops" />} */}

          {search && (
            <WorkshopSearchInput
              displaySearchResult={displaySearchResult}
              searchQuery={searchQuery}
            />
          )}

          <div className={tw('ml-2 flex items-center space-x-4 sm:ml-6 sm:space-x-6')}>
            {/* Profile dropdown */}
            {user?.uid && (
              <>
                {customActions.map((action) => (
                  <Button
                    key={action.text}
                    onClick={action.onClick}
                    text={action.text}
                    size={BUTTON_SIZES.md}
                    type={BUTTON_TYPES.Text}
                  />
                ))}
                {user?.isCouncilMember && (
                  <Button
                    onClick={() => push(NAVIGATION_ROUTES.NewTalent)}
                    text={t('workshop.navbar.new-talent')}
                    size={BUTTON_SIZES.md}
                    type={BUTTON_TYPES.Text}
                  />
                )}

                {asPath === NAVIGATION_ROUTES.Workshops && (
                  <Button
                    onClick={() => push(NAVIGATION_ROUTES.Create)}
                    text={
                      isSmall
                        ? t('workshop.navbar.create-workshop-mobile')
                        : t('workshop.navbar.create-workshop')
                    }
                    size={BUTTON_SIZES.md}
                    type={BUTTON_TYPES.Text}
                  />
                )}
                {!asPath.startsWith(NAVIGATION_ROUTES.Workshops) && (
                  <Button
                    onClick={() => push(NAVIGATION_ROUTES.Workshops)}
                    text={t('workshop.navbar.workshops')}
                    size={BUTTON_SIZES.md}
                    type={BUTTON_TYPES.TextBordered}
                  />
                )}
                <Dropdown
                  onSelect={(option) => option.onClick()}
                  options={userNavigation}
                  rounded
                  position={'right'}>
                  {() => (
                    <>
                      <UserAvatar profilePhoto={user?.profileImageUrl} />
                      <span
                        className={tw(
                          'hidden ml-3 text-gray-700 text-sm font-medium lg:block self-center'
                        )}>
                        <span className="sr-only">Open user menu for </span>
                        {user?.email}
                      </span>
                      <ChevronDownIcon
                        className={tw(
                          'hidden flex-shrink-0 ml-1 h-5 w-5 text-gray-400 lg:block self-center'
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </Dropdown>
              </>
            )}
            {!user?.uid && (
              <div className={tw('md:flex items-center justify-end md:flex-1')}>
                <Button
                  onClick={() => push(NAVIGATION_ROUTES.SignIn)}
                  text={t('commons.sign-in')}
                  size={BUTTON_SIZES.xl}
                  type={BUTTON_TYPES.TextGray}
                  layoutClasses={'mr-8 hidden sm:block'}
                />
                <Button
                  onClick={() => push(NAVIGATION_ROUTES.SignUp)}
                  text={t('commons.sign-up')}
                  size={BUTTON_SIZES.lg}
                  type={BUTTON_TYPES.Primary}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
